import React, { useState, useEffect } from 'react';
import { getFirestore, collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { getStorage, ref, deleteObject } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../config/firebase';
import { signOut } from 'firebase/auth';
import AddButton from '../../components/AddButton/AddButton';
import CreateShopForm from '../../components/CreateShopForm/CreateShopForm';
import EditShopForm from '../../components/EditShopForm/EditShopForm';
import ShopLazyImage from '../../components/ui/ShopLazyImage/ShopLazyImage';
import './Shops.css';

const Shops = () => {
  const navigate = useNavigate();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});
  const [showEditForm, setShowEditForm] = useState(false);
  const [shopToEdit, setShopToEdit] = useState(null);

  const fetchShops = async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      const shopsRef = collection(db, 'shops');
      const snapshot = await getDocs(shopsRef);
      const shopsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setShops(shopsList);
    } catch (error) {
      console.error("Erreur lors de la récupération des boutiques:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShops();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    setIsDeleting(true);
    try {
      const db = getFirestore();
      const storage = getStorage();

      if (itemToDelete.imageUrl) {
        const imageRef = ref(storage, itemToDelete.imageUrl);
        await deleteObject(imageRef).catch(error => {
          console.log("L'image n'existe peut-être plus dans le storage:", error);
        });
      }

      await deleteDoc(doc(db, 'shops', itemToDelete.id));
      await fetchShops();
      
      setShowDeleteConfirm(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Une erreur est survenue lors de la suppression');
    } finally {
      setIsDeleting(false);
    }
  };

  const toggleHours = (shopId) => {
    setExpandedCards(prev => ({
      ...prev,
      [shopId]: !prev[shopId]
    }));
  };

  const handleEditClick = (shop) => {
    setShopToEdit(shop);
    setShowEditForm(true);
  };

  return (
    <div className="shops">
      <div className="shops-content">
        <div className="dashboard-container">
          <main className="main-content">
            <div className="content-header">
              <h1>Boutiques</h1>
              <div className="header-actions">
                <div className="button-container">
                  <AddButton onClick={() => setShowCreateForm(true)} />
                </div>
              </div>
            </div>

            <div className="shops-grid">
              {loading ? (
                <div className="loading">Chargement...</div>
              ) : shops.length === 0 ? (
                <div className="no-results">Aucune boutique enregistrée</div>
              ) : (
                shops.map(shop => (
                  <div key={shop.id} className="shop-card">
                    <div className="shop-image">
                      <ShopLazyImage
                        src={shop.imageUrl} 
                        alt={shop.name}
                        className="shop-card-image"
                      />
                    </div>
                    <div className="shop-content">
                      <h3>{shop.titre}</h3>
                      <div className="shop-info">
                        <p>{shop.address}</p>
                        <p>{shop.phoneNumber}</p>
                        <p>{shop.email}</p>
                      </div>
                      <div className={`shop-hours ${expandedCards[shop.id] ? 'expanded' : ''}`}>
                        <button 
                          className="hours-toggle"
                          onClick={() => toggleHours(shop.id)}
                        >
                          <span>Horaires d'ouverture</span>
                          <i className="fas fa-chevron-down"></i>
                        </button>
                        <div className="hours-content">
                          {shop.hours && (
                            <>
                              {[
                                'monday', 'tuesday', 'wednesday', 
                                'thursday', 'friday', 'saturday', 'sunday'
                              ].map(day => {
                                const dayNames = {
                                  monday: 'Lundi',
                                  tuesday: 'Mardi',
                                  wednesday: 'Mercredi',
                                  thursday: 'Jeudi',
                                  friday: 'Vendredi',
                                  saturday: 'Samedi',
                                  sunday: 'Dimanche'
                                };
                                
                                const hours = shop.hours[day];
                                return (
                                  <p key={day}>
                                    <span className="day-name">{dayNames[day]}: </span>
                                    {!hours ? (
                                      <span className="closed">Fermé</span>
                                    ) : (
                                      <span>
                                        {hours.morning && `${hours.morning.open}-${hours.morning.close}`}
                                        {hours.afternoon && `, ${hours.afternoon.open}-${hours.afternoon.close}`}
                                      </span>
                                    )}
                                  </p>
                                );
                              })}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="shop-actions">
                      <button 
                        className="edit-btn"
                        onClick={() => handleEditClick(shop)}
                      >
                        Modifier
                      </button>
                      <button 
                        className="delete-btn"
                        onClick={() => {
                          setItemToDelete(shop);
                          setShowDeleteConfirm(true);
                        }}
                        disabled={isDeleting}
                      >
                        {isDeleting && itemToDelete?.id === shop.id ? 'Suppression...' : 'Supprimer'}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {showDeleteConfirm && (
              <div className="modal-overlay">
                <div className="confirm-modal">
                  <h3>Confirmer la suppression</h3>
                  <p>Êtes-vous sûr de vouloir supprimer "{itemToDelete?.titre}" ?</p>
                  <p className="warning-text">Cette action est irréversible.</p>
                  <div className="confirm-actions">
                    <button 
                      className="cancel-btn"
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setItemToDelete(null);
                      }}
                      disabled={isDeleting}
                    >
                      Annuler
                    </button>
                    <button 
                      className="delete-btn"
                      onClick={handleDeleteConfirm}
                      disabled={isDeleting}
                    >
                      {isDeleting ? 'Suppression...' : 'Supprimer'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {showCreateForm && (
              <CreateShopForm 
                onClose={() => setShowCreateForm(false)}
                onSuccess={() => {
                  setShowCreateForm(false);
                  fetchShops();
                }}
              />
            )}

            {showEditForm && (
              <EditShopForm 
                shop={shopToEdit}
                onClose={() => {
                  setShowEditForm(false);
                  setShopToEdit(null);
                }}
                onSuccess={() => {
                  setShowEditForm(false);
                  setShopToEdit(null);
                  fetchShops();
                }}
              />
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Shops; 